import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Card,
  Row,
  Col,
  Radio,
  InputNumber,
  Typography,
  Space,
  Alert,
  Divider,
  Table,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { toast } from "react-toastify";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import { getSingleRate } from "../../redux/actions/product/addProductAction";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import data from "../../data/rawMaterialData";
import { buildLayersForPly } from "../../utils/layerUtils";
import { searchOrderItems as searchOrderItemsApi } from "../../api/orderApi";

const { Title, Text } = Typography;

const layerObj = {
  layerId: 0,
  layerType: "",
  materialType: "",
  gsm: "",
  bf: "",
  ff: "",
};

const normalizeNumberOfPieces = (value) => value || 1;

/**
 * OrderItemModal — Simplified item modal for Orders.
 *
 * Props:
 *  - visible, onCancel, onSave, initialData
 *  - pricingType: "individual" | "inner_combined" | "outer_combined"
 */
const OrderItemModal = ({
  visible,
  onCancel,
  onSave,
  initialData,
  pricingType = "individual",
}) => {
  const dispatch = useDispatch();
  const { unitType, ply: plyOptions, castingType } = data;

  const subCategory = useSelector(
    (state) => state.productSubCategories?.list
  );

  const [form] = Form.useForm();

  // Item source toggle
  const [itemSource, setItemSource] = useState("new"); // "new" | "running_size"
  const [runningSizes, setRunningSizes] = useState([]);
  const [loadingRS, setLoadingRS] = useState(false);

  // Layer state
  const [layers, setLayers] = useState([]);
  const [layerSelector, setLayerSelector] = useState(false);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);

  // Printing & coating
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCoating, setIsCoating] = useState(false);
  const [printing, setPrinting] = useState({});
  const [coating, setCoating] = useState({ coatings: [] });
  const [rateTypeRS, setRateTypeRS] = useState("individual");
  const [numberOfPiecesOverride, setNumberOfPiecesOverride] = useState(null);

  // Item Group detection
  const [groupInfo, setGroupInfo] = useState(null);
  const [addAsGroup, setAddAsGroup] = useState(false);

  // Rate calculation
  const [calculationResult, setCalculationResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const calcTimerRef = useRef(null);
  const itemSearchTimerRef = useRef(null);
  const itemSearchRequestRef = useRef(0);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [itemSearchResults, setItemSearchResults] = useState([]);

  // Watched fields for live preview
  const watchedRate = Form.useWatch("rate_per_unit", form);
  const watchedQty = Form.useWatch("quantity", form);
  const watchedQtyPerPack = Form.useWatch("qty_per_pack", form);
  const watchedPackingRole = Form.useWatch("packing_role", form);

  // Watch fields for auto rate calculation
  const watchedSubcat = Form.useWatch("subcategory", form);
  const watchedLength = Form.useWatch("length", form);
  const watchedWidth = Form.useWatch("width", form);
  const watchedHeight = Form.useWatch("height", form);
  const watchedPly = Form.useWatch("ply", form);
  const watchedCasting = Form.useWatch("casting", form);
  const watchedUnitType = Form.useWatch("unitType", form);

  // Auto-trigger rate calculation when mandatory fields are filled
  const triggerRateCalc = useCallback(async () => {
    if (!watchedSubcat || !watchedLength || !watchedWidth || !watchedHeight || !watchedPly || !watchedCasting) return;
    if (layers.length === 0) return;
    // Check all layers have gsm
    const allLayersHaveGsm = layers.every(l => l.gsm && l.gsm > 0);
    if (!allLayersHaveGsm) return;

    const subcat = subCategory?.find(item => item.name === watchedSubcat);
    const formValues = form.getFieldsValue();
    const numberOfPieces = normalizeNumberOfPieces(
      numberOfPiecesOverride || subcat?.sheet_size_formula
    );

    const formData = {
      subcategory: watchedSubcat,
      length: watchedLength,
      width: watchedWidth,
      height: watchedHeight,
      unitType: watchedUnitType || "inches",
      casting: watchedCasting,
      quantity: formValues.quantity || 100,
      ply: watchedPly,
      layerSelector,
      layers,
      isPrinting,
      printing,
      isCoating,
      coating,
      topConversionRate: formValues.topConversionRate || 0,
      boxConversionRate: formValues.boxConversionRate || 0,
      numberOfPieces,
    };

    setCalcLoading(true);
    try {
      const resp = await dispatch(getSingleRate(formData));
      if (resp.message === "success") {
        setCalculationResult(resp.data);
      }
    } catch (e) {
      // Silently fail — this is a reference calculation
    } finally {
      setCalcLoading(false);
    }
  }, [watchedSubcat, watchedLength, watchedWidth, watchedHeight, watchedPly, watchedCasting, watchedUnitType, layers, isPrinting, printing, isCoating, coating, layerSelector, subCategory, numberOfPiecesOverride, dispatch, form]);

  // Debounced auto-calc
  useEffect(() => {
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      triggerRateCalc();
    }, 800);
    return () => clearTimeout(calcTimerRef.current);
  }, [triggerRateCalc]);

  const isCombined = pricingType === "inner_combined" || pricingType === "outer_combined";

  // Load subcategories
  useEffect(() => {
    if (visible) {
      dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
    }
  }, [dispatch, visible]);

  // Fetch running sizes when toggled
  useEffect(() => {
    if (visible && itemSource === "running_size" && runningSizes.length === 0) {
      fetchRunningSizes();
    }
  }, [visible, itemSource]);

  const fetchRunningSizes = async () => {
    setLoadingRS(true);
    try {
      const { data } = await axios.get("running-size?status=true&page=1&count=500");
      setRunningSizes(data?.data || data?.getAllRunningSize || data || []);
    } catch (error) {
      console.error("Failed to load running sizes", error);
    }
    setLoadingRS(false);
  };

  const applyExistingItemToForm = (itemData) => {
    if (!itemData) return;

    if (itemData.ply) {
      handlePlyChange(itemData.ply);
    }

    let parsedLayers = [];
    let parsedPrinting = {};
    let parsedCoating = { coatings: [] };

    if (itemData.layers) {
      try {
        parsedLayers =
          typeof itemData.layers === "string"
            ? JSON.parse(itemData.layers)
            : itemData.layers;
      } catch {
        parsedLayers = [];
      }
    }
    if (itemData.printing_config) {
      try {
        parsedPrinting =
          typeof itemData.printing_config === "string"
            ? JSON.parse(itemData.printing_config)
            : itemData.printing_config;
      } catch {
        parsedPrinting = {};
      }
    }
    if (itemData.coating_config) {
      try {
        parsedCoating =
          typeof itemData.coating_config === "string"
            ? JSON.parse(itemData.coating_config)
            : itemData.coating_config;
      } catch {
        parsedCoating = { coatings: [] };
      }
    }

    setLayers(parsedLayers);
    setPrinting(parsedPrinting);
    setCoating(parsedCoating);
    setRateTypeRS(itemData.rate_type || "individual");
    setIsPrinting(itemData.is_printing || false);
    setIsCoating(itemData.is_coating || false);
    setNumberOfPiecesOverride(normalizeNumberOfPieces(itemData.number_of_pieces));

    const formValues = {
      item_name: itemData.item_name,
      item_description: itemData.item_description,
      subcategory: itemData.subcategory,
      length: itemData.length,
      width: itemData.width,
      height: itemData.height,
      unitType: itemData.unit_type,
      casting: itemData.casting_type,
      ply: itemData.ply,
      quantity: itemData.quantity,
      rate_per_unit: itemData.rate_per_unit,
      topConversionRate: itemData.top_conversion_rate,
      boxConversionRate: itemData.box_conversion_rate,
      layerSelector: "basic",
      print_selector: itemData.is_printing ? "yes" : "no",
      isCoating: itemData.is_coating ? "yes" : "no",
      packing_role: itemData.packing_role || "inner",
      qty_per_pack: itemData.qty_per_pack,
      rs_val: itemData.rs,
      cs_val: itemData.cs,
      boxesPerSheet: itemData.boxes_per_sheet,
      sheetsPerBox: itemData.sheets_per_box,
      boxType: itemData.box_type || undefined,
      rateTypeRS: itemData.rate_type || "individual",
      packingQuantityRS: itemData.packing_quantity_rs || undefined,
      joiningMethod: itemData.joining_method || undefined,
      itemRemarks: itemData.item_remarks || "",
    };

    if (parsedLayers.length >= 1) {
      formValues.materialType = parsedLayers[0]?.materialType || undefined;
      formValues.top_gsm = parsedLayers[0]?.gsm || undefined;
      formValues["top-bf"] = parsedLayers[0]?.bf || undefined;
    }
    if (parsedLayers.length >= 2) {
      formValues.flute_materialType = parsedLayers[1]?.materialType || undefined;
      formValues.flute_gsm = parsedLayers[1]?.gsm || undefined;
      formValues.flute_bf = parsedLayers[1]?.bf || undefined;
      formValues.flute_ff = parsedLayers[1]?.ff || undefined;
    }
    if (parsedLayers.length >= 3) {
      formValues.face_materialType = parsedLayers[2]?.materialType || undefined;
      formValues.face_gsm = parsedLayers[2]?.gsm || undefined;
      formValues.face_bf = parsedLayers[2]?.bf || undefined;
    }

    if (itemData.is_printing && parsedPrinting) {
      formValues.printing_type = parsedPrinting.printingType || undefined;
      formValues.printing_colors = parsedPrinting.printingColors || undefined;
      formValues.printing_sides = parsedPrinting.printingSides || undefined;
      formValues.printing_special_colors = parsedPrinting.printingSpecialColors || undefined;
    }

    if (itemData.is_coating && parsedCoating) {
      formValues.coating_type = parsedCoating.coatings || undefined;
    }

    form.setFieldsValue(formValues);
  };

  const formatSearchResult = (item) => {
    const dimensions = [item.length, item.width, item.height]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .join(" x ");

    return [
      item.item_name,
      item.subcategory,
      dimensions ? `${dimensions} ${item.unit_type || ""}`.trim() : "",
      item.ply ? `${item.ply} Ply` : "",
      item.order?.order_number || "",
      item.order?.customer?.name || "",
    ]
      .filter(Boolean)
      .join(" | ");
  };

  const handleItemNameSearch = (value) => {
    if (itemSearchTimerRef.current) {
      clearTimeout(itemSearchTimerRef.current);
    }

    if (itemSource !== "new" || !value || value.trim().length < 2) {
      setItemSearchResults([]);
      setItemSearchLoading(false);
      return;
    }

    itemSearchTimerRef.current = setTimeout(async () => {
      const requestId = itemSearchRequestRef.current + 1;
      itemSearchRequestRef.current = requestId;
      setItemSearchLoading(true);

      try {
        const response = await searchOrderItemsApi({
          search: value.trim(),
          limit: 8,
        });

        if (itemSearchRequestRef.current !== requestId) return;
        setItemSearchResults(response?.data || []);
      } catch (error) {
        if (itemSearchRequestRef.current === requestId) {
          setItemSearchResults([]);
        }
      } finally {
        if (itemSearchRequestRef.current === requestId) {
          setItemSearchLoading(false);
        }
      }
    }, 350);
  };

  const handleExistingItemSelect = (itemData) => {
    setItemSearchResults([]);
    applyExistingItemToForm(itemData);
  };


  // Auto-fill form from selected running size
  const handleRunningSizeSelect = async (rsId) => {
    const rs = runningSizes.find((r) => r.id === rsId);
    if (!rs) return;
    setNumberOfPiecesOverride(null);

    // 1. Handle ply change FIRST to set up layer structure
    if (rs.ply) {
      handlePlyChange(rs.ply);
    }

    // 2. Parse layers, printing, coating from RS data
    let parsedLayers = [];
    let parsedPrinting = {};
    let parsedCoating = { coatings: [] };

    if (rs.layers) {
      try {
        parsedLayers = typeof rs.layers === "string" ? JSON.parse(rs.layers) : rs.layers;
      } catch { parsedLayers = []; }
    }
    if (rs.printing) {
      try {
        parsedPrinting = typeof rs.printing === "string" ? JSON.parse(rs.printing) : rs.printing;
      } catch { parsedPrinting = {}; }
    }
    if (rs.coating) {
      try {
        parsedCoating = typeof rs.coating === "string" ? JSON.parse(rs.coating) : rs.coating;
      } catch { parsedCoating = { coatings: [] }; }
    }

    // 3. Set parsed layers AFTER handlePlyChange (overwrite the empty template)
    setLayers(parsedLayers);
    setIsPrinting(rs.is_printing || false);
    setIsCoating(rs.is_coating || false);
    setPrinting(parsedPrinting);
    setCoating(parsedCoating);
    setRateTypeRS(rs.rate_type || "individual");

    // 4. Build form field values including all layer/print/coat form field names
    const formValues = {
      item_name: rs.name,
      subcategory: rs.product_sub_category?.name || "",
      length: rs.length,
      width: rs.width,
      height: rs.height,
      unitType: rs.unit_type,
      casting: rs.casting_type,
      ply: rs.ply,
      topConversionRate: rs.top_conversion_rate,
      boxConversionRate: rs.box_conversion_rate,
      rate_per_unit: rs.last_sale_price || 0,
      print_selector: rs.is_printing ? "yes" : "no",
      isCoating: rs.is_coating ? "yes" : "no",
      rs_val: rs.rs,
      cs_val: rs.cs,
      boxesPerSheet: rs.boxes_per_sheet,
      sheetsPerBox: rs.sheets_per_box,
      boxType: rs.box_type || undefined,
      rateTypeRS: rs.rate_type || "individual",
      packingQuantityRS: rs.packing_quantity || undefined,
      joiningMethod: rs.joining_method || undefined,
      itemRemarks: rs.remarks || "",
    };

    // Layer form fields (LayerSelectorBasic uses these form names)
    if (parsedLayers.length >= 1) {
      formValues.materialType = parsedLayers[0]?.materialType || undefined;
      formValues.top_gsm = parsedLayers[0]?.gsm || undefined;
      formValues["top-bf"] = parsedLayers[0]?.bf || undefined;
    }
    if (parsedLayers.length >= 2) {
      formValues.flute_materialType = parsedLayers[1]?.materialType || undefined;
      formValues.flute_gsm = parsedLayers[1]?.gsm || undefined;
      formValues.flute_bf = parsedLayers[1]?.bf || undefined;
      formValues.flute_ff = parsedLayers[1]?.ff || undefined;
    }
    if (parsedLayers.length >= 3) {
      formValues.face_materialType = parsedLayers[2]?.materialType || undefined;
      formValues.face_gsm = parsedLayers[2]?.gsm || undefined;
      formValues.face_bf = parsedLayers[2]?.bf || undefined;
    }

    // Printing form fields
    if (rs.is_printing && parsedPrinting) {
      formValues.printing_type = parsedPrinting.printingType || undefined;
      formValues.printing_colors = parsedPrinting.printingColors || undefined;
      formValues.printing_sides = parsedPrinting.printingSides || undefined;
      formValues.printing_special_colors = parsedPrinting.printingSpecialColors || undefined;
    }

    // Coating form fields
    if (rs.is_coating && parsedCoating) {
      formValues.coating_type = parsedCoating.coatings || undefined;
    }

    form.setFieldsValue(formValues);

    // Check if this RS is the parent of any item group
    setGroupInfo(null);
    setAddAsGroup(false);
    try {
      const { data: grp } = await axios.get(`item-group/by-parent/${rsId}`);
      if (grp) {
        setGroupInfo(grp);
        setAddAsGroup(true);
      }
    } catch {
      // No group — that's fine
    }
  };


  // Reset / pre-fill when modal opens
  useEffect(() => {
    if (visible) {
      if (initialData) {
        setItemSource("new");
        
        // Parse layers, printing, coating first
        let parsedLayers = [];
        let parsedPrinting = {};
        let parsedCoating = { coatings: [] };

        if (initialData.layers) {
          try {
            parsedLayers =
              typeof initialData.layers === "string"
                ? JSON.parse(initialData.layers)
                : initialData.layers;
          } catch {
            parsedLayers = [];
          }
        }
        // A brand-new item (no matched RunningSize — e.g. resolved from PO extraction) has no
        // known layer construction, so `initialData.layers` is empty here. Unlike picking Ply
        // by hand (which runs buildLayersForPly via handlePlyChange), setFieldsValue below does
        // not fire that onChange, so without this the layers array would stay empty while `ply`
        // shows a value — leaving the Customization panel with no rows to fill in at all, and
        // an inconsistent ply/layers pair if saved as-is. Seed the correct blank layer slots
        // (Top/Flute/Face/... matching the ply count) so the shape is right; values are still
        // left for the operator to fill in, since nothing on a PO ever states GSM/paper type.
        if (parsedLayers.length === 0 && initialData.ply) {
          parsedLayers = buildLayersForPly(initialData.ply, [], layerObj).layersArray;
        }
        if (initialData.printing_config) {
          try {
            parsedPrinting =
              typeof initialData.printing_config === "string"
                ? JSON.parse(initialData.printing_config)
                : initialData.printing_config;
          } catch {
            parsedPrinting = {};
          }
        }
        if (initialData.coating_config) {
          try {
            parsedCoating =
              typeof initialData.coating_config === "string"
                ? JSON.parse(initialData.coating_config)
                : initialData.coating_config;
          } catch {
            parsedCoating = { coatings: [] };
          }
        }

        // Set state
        setLayers(parsedLayers);
        setPrinting(parsedPrinting);
        setCoating(parsedCoating);
        setRateTypeRS(initialData.rate_type || "individual");
        setIsPrinting(initialData.is_printing || false);
        setIsCoating(initialData.is_coating || false);

        // Build form values including layer/print/coat fields
        const formValues = {
          item_name: initialData.item_name,
          item_description: initialData.item_description,
          subcategory: initialData.subcategory,
          length: initialData.length,
          width: initialData.width,
          height: initialData.height,
          unitType: initialData.unit_type,
          casting: initialData.casting_type,
          ply: initialData.ply,
          quantity: initialData.quantity,
          rate_per_unit: initialData.rate_per_unit,
          topConversionRate: initialData.top_conversion_rate,
          boxConversionRate: initialData.box_conversion_rate,
          layerSelector: "basic",
          print_selector: initialData.is_printing ? "yes" : "no",
          isCoating: initialData.is_coating ? "yes" : "no",
          packing_role: initialData.packing_role || "inner",
          qty_per_pack: initialData.qty_per_pack,
          rs_val: initialData.rs,
          cs_val: initialData.cs,
          boxesPerSheet: initialData.boxes_per_sheet,
          sheetsPerBox: initialData.sheets_per_box,
          boxType: initialData.box_type || undefined,
          rateTypeRS: initialData.rate_type || "individual",
          packingQuantityRS: initialData.packing_quantity_rs || undefined,
          joiningMethod: initialData.joining_method || undefined,
          itemRemarks: initialData.item_remarks || "",
        };

        // Layer form fields (LayerSelectorBasic uses these form names)
        if (parsedLayers.length >= 1) {
          formValues.materialType = parsedLayers[0]?.materialType || undefined;
          formValues.top_gsm = parsedLayers[0]?.gsm || undefined;
          formValues["top-bf"] = parsedLayers[0]?.bf || undefined;
        }
        if (parsedLayers.length >= 2) {
          formValues.flute_materialType = parsedLayers[1]?.materialType || undefined;
          formValues.flute_gsm = parsedLayers[1]?.gsm || undefined;
          formValues.flute_bf = parsedLayers[1]?.bf || undefined;
          formValues.flute_ff = parsedLayers[1]?.ff || undefined;
        }
        if (parsedLayers.length >= 3) {
          formValues.face_materialType = parsedLayers[2]?.materialType || undefined;
          formValues.face_gsm = parsedLayers[2]?.gsm || undefined;
          formValues.face_bf = parsedLayers[2]?.bf || undefined;
        }

        // Printing form fields
        if (initialData.is_printing && parsedPrinting) {
          formValues.printing_type = parsedPrinting.printingType || undefined;
          formValues.printing_colors = parsedPrinting.printingColors || undefined;
          formValues.printing_sides = parsedPrinting.printingSides || undefined;
          formValues.printing_special_colors = parsedPrinting.printingSpecialColors || undefined;
        }

        // Coating form fields
        if (initialData.is_coating && parsedCoating) {
          formValues.coating_type = parsedCoating.coatings || undefined;
        }

        form.setFieldsValue(formValues);
      } else {
        // New item — reset
        form.resetFields();
        setItemSource("new");
        setLayers([]);
        setGsm([]);
        setBf([]);
        setIsPrinting(false);
        setIsCoating(false);
        setPrinting({});
        setCoating({ coatings: [] });
        setLayerSelector(false);
        setRateTypeRS("individual");
        setNumberOfPiecesOverride(null);
        setCalculationResult(null);
        setItemSearchResults([]);
        setGroupInfo(null);
        setAddAsGroup(false);
      }
    }
  }, [visible, initialData, form]);

  useEffect(() => () => {
    if (itemSearchTimerRef.current) clearTimeout(itemSearchTimerRef.current);
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
  }, []);

  // Ply change handler – preserves existing layer values
  const handlePlyChange = (plyVal) => {
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(plyVal, layers, layerObj);
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  // Save handler
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Determine packing role and qty_per_pack
      const packingRole = isCombined
        ? (values.packing_role || "inner")
        : (values.packing_role || null);
      const qtyPerPack = packingRole === "inner"
        ? Number(values.qty_per_pack || 1)
        : (packingRole === "outer" ? 1 : null);

      // Calculate quantity based on pricing type
      const quantity = isCombined
        ? (qtyPerPack || 1)
        : Number(values.quantity);

      const ratePerUnit =
        pricingType === "individual"
          ? Number(values.rate_per_unit || 0)
          : 0;

      const totalAmount =
        pricingType === "individual" ? quantity * ratePerUnit : 0;
      const selectedSubcategory = subCategory?.find(
        (item) => item.name === values.subcategory
      );
      const numberOfPieces = normalizeNumberOfPieces(
        numberOfPiecesOverride || selectedSubcategory?.sheet_size_formula
      );

      const itemData = {
        // Display
        item_name:
          values.item_name ||
          `${values.subcategory} - ${values.length}×${values.width}×${values.height}`,
        item_description: values.item_description || "",

        // Box specification
        subcategory: values.subcategory,
        number_of_pieces: numberOfPieces,
        length: Number(values.length),
        width: Number(values.width),
        height: Number(values.height),
        unit_type: values.unitType,
        casting_type: values.casting,
        ply: Number(values.ply),
        layers: layers,

        // Printing
        is_printing: isPrinting,
        printing_config: printing,

        // Coating
        is_coating: isCoating,
        coating_config: coating,

        // Finishing
        joining_method: values.joiningMethod || null,

        // Conversion rates
        top_conversion_rate: Number(values.topConversionRate || 0),
        box_conversion_rate: Number(values.boxConversionRate || 0),

        // Running size derived fields
        rs: values.rs_val ? Number(values.rs_val) : null,
        cs: values.cs_val ? Number(values.cs_val) : null,
        boxes_per_sheet: values.boxesPerSheet ? Number(values.boxesPerSheet) : null,
        sheets_per_box: values.sheetsPerBox ? Number(values.sheetsPerBox) : null,
        box_type: values.boxType || null,
        rate_type: values.rateTypeRS || null,
        packing_quantity_rs: values.packingQuantityRS ? Number(values.packingQuantityRS) : null,
        item_remarks: values.itemRemarks || null,

        // Pricing
        quantity: quantity,
        rate_per_unit: ratePerUnit,
        total_amount: totalAmount,

        // Combined packing fields
        packing_role: packingRole,
        qty_per_pack: qtyPerPack,

        // Production spec fields — defaults
        calculated_rate: 0,
        markup_percentage: 0,
        discount_percentage: 0,
        roll_size: null,
        cutting_size: null,
        net_gsm: null,
        top_gsm: null,
        box_weight: null,
        material_cost_top: 0,
        material_cost_rest: 0,
        print_cost: 0,
        coating_cost_total: 0,
        coating_cost_lamination: 0,
        coating_cost_leafing: 0,
        coating_cost_spot_uv: 0,
        fixed_cost_total: 0,
        fixed_cost_per_box: 0,
        selected_machines: null,
        outsource_jobs: null,

        // Group fields
        item_group_id: (groupInfo && addAsGroup) ? groupInfo.id : null,
        qty_per_group: (groupInfo && addAsGroup)
          ? ((groupInfo.members || []).find((m) => m.is_parent)?.qty_per_group || 1)
          : null,
        is_group_parent: !!(groupInfo && addAsGroup),
      };

      if (groupInfo && addAsGroup) {
        const parentMember = (groupInfo.members || []).find((m) => m.is_parent);
        const parentQtyPerGroup = parentMember?.qty_per_group || 1;

        const memberItems = (groupInfo.members || [])
          .filter((m) => !m.is_parent)
          .map((m) => {
            const rs = m.running_size;
            const rate = rs?.last_sale_price || 0;
            const memberQty = Math.round(quantity * (m.qty_per_group / parentQtyPerGroup));
            return {
              item_name: m.name,
              item_description: `Part of group: ${groupInfo.name}`,
              subcategory: rs?.product_sub_category?.name || "",
              number_of_pieces: 1,
              length: rs?.length || 0,
              width: rs?.width || 0,
              height: rs?.height || 0,
              unit_type: rs?.unit_type || "mm",
              casting_type: rs?.casting_type || "DIE",
              ply: rs?.ply || 3,
              layers: rs?.layers || "[]",
              is_printing: rs?.is_printing || false,
              printing_config: rs?.printing || null,
              is_coating: rs?.is_coating || false,
              coating_config: rs?.coating || null,
              joining_method: rs?.joining_method || null,
              top_conversion_rate: rs?.top_conversion_rate || 0,
              box_conversion_rate: rs?.box_conversion_rate || 0,
              rs: rs?.rs || null,
              cs: rs?.cs || null,
              boxes_per_sheet: rs?.boxes_per_sheet || null,
              sheets_per_box: rs?.sheets_per_box || null,
              box_type: rs?.box_type || null,
              rate_type: rs?.rate_type || null,
              packing_quantity_rs: null,
              item_remarks: null,
              quantity: memberQty,
              rate_per_unit: rate,
              total_amount: rate * memberQty,
              packing_role: null,
              qty_per_pack: null,
              calculated_rate: 0,
              markup_percentage: 0,
              discount_percentage: 0,
              roll_size: null,
              cutting_size: null,
              net_gsm: null,
              top_gsm: null,
              box_weight: null,
              material_cost_top: 0,
              material_cost_rest: 0,
              print_cost: 0,
              coating_cost_total: 0,
              coating_cost_lamination: 0,
              coating_cost_leafing: 0,
              coating_cost_spot_uv: 0,
              fixed_cost_total: 0,
              fixed_cost_per_box: 0,
              selected_machines: null,
              outsource_jobs: null,
              item_group_id: groupInfo.id,
              qty_per_group: m.qty_per_group,
              is_group_parent: false,
            };
          });

        onSave([itemData, ...memberItems]);
      } else {
        onSave(itemData);
      }
    } catch (error) {
      if (error.errorFields) {
        toast.error("Please fill in all required fields");
      }
    }
  };

  // Computed production qty for combined mode
  const effectiveQtyPerPack = watchedPackingRole === "inner" ? (watchedQtyPerPack || 0) : 1;

  // Computed total for individual mode
  const liveTotal =
    pricingType === "individual"
      ? (Number(watchedRate) || 0) * (Number(watchedQty) || 0)
      : 0;

  return (
    <Drawer
      title={initialData ? "Edit Order Item" : "Add Order Item"}
      open={visible}
      onClose={onCancel}
      placement="right"
      width={720}
      destroyOnClose
    >
      <Form
        form={form}
        name="order_item"
        labelCol={{ span: 8 }}
        labelWrap
        wrapperCol={{ span: 16 }}
        initialValues={{
          layerSelector: "basic",
          print_selector: "no",
          isCoating: "no",
          packing_role: "inner",
        }}
        autoComplete="off"
      >
        {/* ====== Item Source Toggle ====== */}
        <Card
          bordered={false}
          style={{ marginBottom: 16, background: "#f0f5ff" }}
        >
          <Row gutter={16} align="middle">
            <Col xs={24} md={8}>
              <Form.Item label="Item Source" style={{ marginBottom: 0 }}>
                <Radio.Group
                  value={itemSource}
                  onChange={(e) => {
                    setItemSource(e.target.value);
                    setItemSearchResults([]);
                    if (e.target.value === "new") {
                      form.resetFields();
                    }
                  }}
                >
                  <Radio value="new">New Item</Radio>
                  <Radio value="running_size">Running Size</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {itemSource === "running_size" && (
              <Col xs={24} md={16}>
                <Select
                  showSearch
                  placeholder="Search running size by name, dimensions..."
                  style={{ width: "100%" }}
                  loading={loadingRS}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleRunningSizeSelect}
                  size="large"
                >
                  {runningSizes.map((rs) => {
                    const label = `${rs.name} — ${rs.length}×${rs.width}×${rs.height} ${rs.unit_type || ""} | ${rs.ply} Ply | ${rs.product_sub_category?.name || ""} | ${rs.customer?.name || ""}`;
                    return (
                      <Select.Option key={rs.id} value={rs.id} label={label}>
                        {label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
            )}
          </Row>
        </Card>

        {/* ====== Group Detection Banner ====== */}
        {groupInfo && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Space>
                <strong>{groupInfo.name}</strong>
                <span>— This item is the parent of an item group.</span>
                <Switch
                  checked={addAsGroup}
                  onChange={setAddAsGroup}
                  size="small"
                  checkedChildren="Group ON"
                  unCheckedChildren="Group OFF"
                />
              </Space>
            }
            description={
              addAsGroup ? (
                <div style={{ marginTop: 8 }}>
                  <span style={{ marginRight: 8 }}>Saving will add all group members:</span>
                  {(groupInfo.members || []).map((m, i) => (
                    <Tooltip key={i} title={`${m.qty_per_group} × ${m.name} (uses last sale price)`}>
                      <Tag color={m.is_parent ? "blue" : "default"}>
                        {m.qty_per_group}× {m.name}{m.is_parent ? " (parent)" : ""}
                      </Tag>
                    </Tooltip>
                  ))}
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    Non-parent members use their last sale price. Rates can be updated individually.
                  </div>
                </div>
              ) : (
                <span style={{ fontSize: 12, color: "#888" }}>Toggle ON to auto-add all group members.</span>
              )
            }
          />
        )}

        {/* ====== Item Naming ====== */}
        <Card
          bordered={false}
          style={{ marginBottom: 16, background: "#fafafa" }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="item_name"
                label="Item Name"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
              >
                <Input
                  placeholder="Type item name to search previous order items"
                  onChange={(e) => handleItemNameSearch(e.target.value)}
                />
              </Form.Item>
              {itemSource === "new" && (itemSearchLoading || itemSearchResults.length > 0) && (
                <div
                  style={{
                    marginLeft: "25%",
                    marginTop: -8,
                    padding: 8,
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    background: "#fff",
                    maxHeight: 180,
                    overflowY: "auto",
                  }}
                >
                  {itemSearchLoading && (
                    <Text type="secondary">Searching previous order items...</Text>
                  )}
                  {!itemSearchLoading &&
                    itemSearchResults.map((item) => (
                      <div
                        key={item.id}
                        onMouseDown={() => handleExistingItemSelect(item)}
                        style={{
                          padding: "6px 8px",
                          cursor: "pointer",
                          borderRadius: 4,
                        }}
                      >
                        {formatSearchResult(item)}
                      </div>
                    ))}
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="item_description"
                label="Description"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
              >
                <Input placeholder="Short description" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row gutter={16}>
          {/* ====== Left Column: Sizing ====== */}
          <Col xs={24} lg={10}>
            <Card bordered={false}>
              <Title level={5} className="m-2 text-center">
                Sizing
              </Title>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="subcategory"
                label="Subcategory"
                rules={[
                  { required: true, message: "Please select sub-category!" },
                ]}
              >
                <Select
                  loading={!subCategory}
                  showSearch
                  placeholder="Select Subcategory"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {subCategory &&
                    subCategory.map((subcat) => (
                      <Select.Option key={subcat.id} value={subcat.name}>
                        {subcat.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Length"
                name="length"
                rules={[{ required: true, message: "Please input length!" }]}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Width"
                name="width"
                rules={[{ required: true, message: "Please input width!" }]}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Height"
                name="height"
                rules={[{ required: true, message: "Please input height!" }]}
              >
                <Input type="number" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="unitType"
                label="Unit"
                rules={[{ required: true, message: "Please select unit type!" }]}
              >
                <Select
                  showSearch
                  placeholder="Unit"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.includes(input)
                  }
                >
                  {unitType &&
                    unitType.map((unit) => (
                      <Select.Option key={unit} value={unit}>
                        {unit}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="casting"
                label="Casting"
                rules={[{ required: true, message: "Please select casting!" }]}
              >
                <Select
                  showSearch
                  placeholder="Casting"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children.includes(input)
                  }
                >
                  {castingType &&
                    castingType.map((casting, i) => (
                      <Select.Option key={i + 1} value={casting.value}>
                        {casting.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              {/* ====== Packing Role & Quantity ====== */}
              {isCombined ? (
                <>
                  <Form.Item
                    style={{ marginBottom: 15 }}
                    name="packing_role"
                    label="Packing Role"
                    rules={[{ required: true }]}
                  >
                    <Select
                      onChange={(val) => {
                        if (val === "outer") {
                          form.setFieldsValue({ qty_per_pack: 1 });
                        }
                      }}
                    >
                      <Select.Option value="inner">Inner Box</Select.Option>
                      <Select.Option value="outer">Outer Carton</Select.Option>
                    </Select>
                  </Form.Item>

                  {watchedPackingRole === "inner" && (
                    <Form.Item
                      style={{ marginBottom: 15 }}
                      label="Qty per Pack"
                      name="qty_per_pack"
                      rules={[{ required: true, message: "Enter quantity per pack unit" }]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  )}

                </>
              ) : (
                <>
                  <Form.Item
                    style={{ marginBottom: 15 }}
                    label="Quantity"
                    name="quantity"
                    rules={[{ required: true, message: "Please input Quantity!" }]}
                  >
                    <Input type="number" />
                  </Form.Item>
                </>
              )}

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Top Conversion Rate /Kg"
                name="topConversionRate"
              >
                <Input type="number" placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Box Conversion Rate /Kg"
                name="boxConversionRate"
              >
                <Input type="number" placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="RS"
                name="rs_val"
              >
                <Input type="number" placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="CS"
                name="cs_val"
              >
                <Input type="number" placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Boxes per Sheet (m)"
                name="boxesPerSheet"
              >
                <Input type="number" min={1} placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Sheets per Box (n)"
                name="sheetsPerBox"
              >
                <Input type="number" min={1} placeholder="Optional" />
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="boxType"
                label="Box Type"
              >
                <Select placeholder="Select Box Type" allowClear>
                  <Select.Option value="inner">Inner</Select.Option>
                  <Select.Option value="outer">Outer</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="rateTypeRS"
                label="Rate Type"
              >
                <Select placeholder="Select Rate Type" onChange={(val) => setRateTypeRS(val)}>
                  <Select.Option value="individual">Individual</Select.Option>
                  <Select.Option value="innerwise">Innerwise</Select.Option>
                  <Select.Option value="outerwise">Outerwise</Select.Option>
                </Select>
              </Form.Item>

              {rateTypeRS && rateTypeRS !== "individual" && (
                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="No. of Inners"
                  name="packingQuantityRS"
                >
                  <Input type="number" min={1} placeholder="No. of inners per outer" />
                </Form.Item>
              )}

              <Form.Item
                style={{ marginBottom: 15 }}
                name="joiningMethod"
                label="Glueing"
              >
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="stitching">Stitching</Radio.Button>
                  <Radio.Button value="hand_pasting">Hand Pasting</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                style={{ marginBottom: 15 }}
                label="Remarks"
                name="itemRemarks"
              >
                <Input.TextArea rows={2} placeholder="Optional remarks" />
              </Form.Item>
            </Card>
          </Col>

          {/* ====== Right Column: Customization ====== */}
          <Col xs={24} lg={14}>
            <Card bordered={false}>
              <Title level={5} className="m-2 text-center">
                Customization
              </Title>

              <Form.Item
                style={{ marginBottom: 15 }}
                name="ply"
                label="Ply / Walls"
                rules={[{ required: true, message: "Please select plys!" }]}
              >
                <Select
                  showSearch
                  placeholder="Ply / Walls"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.includes(input)
                  }
                  onChange={handlePlyChange}
                >
                  {plyOptions &&
                    plyOptions.map((p) => (
                      <Select.Option key={p.value} value={p.value}>
                        {p.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <LayerTemplateSelector
                ply={layers.length}
                layers={layers}
                setLayers={setLayers}
                gsm={gsm}
                setGsm={setGsm}
                bf={bf}
                setBf={setBf}
              />

              <Form.Item
                label="Layer Selector"
                name="layerSelector"
                style={{ marginBottom: 15 }}
              >
                <Radio.Group
                  defaultValue="basic"
                  onChange={(e) =>
                    setLayerSelector(e.target.value === "advance")
                  }
                >
                  <Radio value="basic">Basic</Radio>
                  <Radio value="advance">Advance</Radio>
                </Radio.Group>
              </Form.Item>

              {!layerSelector ? (
                <LayerSelectorBasic
                  layers={layers}
                  setLayers={setLayers}
                  layerSelector={layerSelector}
                />
              ) : (
                <LayerSelectorAdvance
                  layers={layers}
                  gsm={gsm}
                  bf={bf}
                  setLayers={setLayers}
                  setGsm={setGsm}
                  setBf={setBf}
                  layerSelector={layerSelector}
                />
              )}

              <Form.Item
                label="Printing"
                name="print_selector"
                style={{ marginBottom: 15 }}
              >
                <Radio.Group
                  defaultValue="no"
                  onChange={(e) => setIsPrinting(e.target.value === "yes")}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Radio.Group>
              </Form.Item>

              {isPrinting && (
                <PrintSelector
                  printing={printing}
                  setPrinting={setPrinting}
                  isPrinting={isPrinting}
                />
              )}

              <Form.Item
                label="Coating"
                name="isCoating"
                style={{ marginBottom: 15 }}
              >
                <Radio.Group
                  defaultValue="no"
                  onChange={(e) => setIsCoating(e.target.value === "yes")}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Radio.Group>
              </Form.Item>

              {isCoating && (
                <CoatingSelector
                  coating={coating}
                  setCoating={setCoating}
                  isCoating={isCoating}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* ====== Rate Input — Individual Pricing Only ====== */}
        {pricingType === "individual" && (
          <Card
            bordered={false}
            style={{ marginTop: 16, background: "#f6ffed" }}
          >
            <Title level={5}>Pricing</Title>
            <Row gutter={16} align="middle">
              <Col xs={24} md={8}>
                <Form.Item
                  name="rate_per_unit"
                  label="Rate / Unit (₹)"
                  labelCol={{ span: 12 }}
                  wrapperCol={{ span: 12 }}
                  rules={[{ required: true, message: "Enter rate per unit" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    placeholder="₹0.00"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Quantity: </Text>
                <Text strong>{watchedQty || 0}</Text>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Total: </Text>
                <Text strong style={{ fontSize: 18, color: "#1890ff" }}>
                  ₹{liveTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {/* ====== Info for Combined Packing ====== */}
        {isCombined && (
          <Alert
            message={pricingType === "inner_combined" ? "Inner-wise Combined Pricing" : "Outer-wise Combined Pricing"}
            description="Rate is set at the order level. Add all inner and outer items that make up one pack."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {/* ====== Rate Calculation Reference ====== */}
        {calculationResult && (
          <Card
            bordered
            size="small"
            style={{ marginTop: 16, background: "#fffbe6", borderColor: "#ffe58f" }}
            title={
              <Space>
                <span style={{ fontSize: 14, fontWeight: 600 }}>📊 Calculated Rate Reference</span>
                {calcLoading && <Text type="secondary" style={{ fontSize: 11 }}>Recalculating...</Text>}
              </Space>
            }
            extra={
              <Button size="small" type="link" onClick={triggerRateCalc} loading={calcLoading}>
                Recalculate
              </Button>
            }
          >
            <Row gutter={[12, 8]} style={{ marginBottom: 12 }}>
              <Col xs={8} md={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>RS × CS</Text>
                <div style={{ fontWeight: 600 }}>{calculationResult.RS}" × {calculationResult.CS}"</div>
              </Col>
              <Col xs={8} md={3}>
                <Text type="secondary" style={{ fontSize: 11 }}>M (Boxes/Sheet)</Text>
                <div style={{ fontWeight: 600 }}>{calculationResult.M}</div>
              </Col>
              <Col xs={8} md={3}>
                <Text type="secondary" style={{ fontSize: 11 }}>N (Sheets/Box)</Text>
                <div style={{ fontWeight: 600 }}>{calculationResult.N}</div>
              </Col>
              <Col xs={8} md={3}>
                <Text type="secondary" style={{ fontSize: 11 }}>Net GSM</Text>
                <div style={{ fontWeight: 600 }}>{calculationResult.NET_GSM}</div>
              </Col>
              <Col xs={8} md={3}>
                <Text type="secondary" style={{ fontSize: 11 }}>Box Wt</Text>
                <div style={{ fontWeight: 600 }}>{Number(calculationResult.boxWeight || 0).toFixed(3)} Kg</div>
              </Col>
              <Col xs={8} md={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>Net Fixed Cost</Text>
                <div style={{ fontWeight: 600 }}>₹{Number(calculationResult.netFixedCost || 0).toLocaleString("en-IN")}</div>
              </Col>
            </Row>

            {/* Detailed Rate Breakup Table — same as Rate Calculator */}
            <Title level={5} style={{ marginBottom: 8 }}>Detailed Rate Breakup</Title>
            {(() => {
              const topRate = Number(calculationResult.topRate || 0);
              const restRate = Number(calculationResult.restRate || 0);
              const printRate = Number(calculationResult.printRate || calculationResult.printingCost || 0);
              const fixedCost = Number(calculationResult.fixedCostPerBox || 0);
              const laminationRate = Number(calculationResult.laminationRate || 0);
              const leafingRate = Number(calculationResult.leafingRate || 0);
              const spotUvRate = Number(calculationResult.spotUvRate || 0);
              const finalRate = Number(calculationResult.finalRate || 0);

              const rateData = [
                { key: 1, parameter: "Top Layer Rate", value: topRate, total: topRate },
                { key: 2, parameter: "Rest Layers Rate", value: restRate, total: topRate + restRate },
                { key: 3, parameter: "Printing Cost", value: printRate, total: topRate + restRate + printRate },
                { key: 4, parameter: "Fixed Cost", value: fixedCost, total: topRate + restRate + printRate + fixedCost },
                { key: 5, parameter: "Lamination Rate", value: laminationRate, total: topRate + restRate + printRate + fixedCost + laminationRate },
                { key: 6, parameter: "Leafing Rate", value: leafingRate, total: topRate + restRate + printRate + fixedCost + laminationRate + leafingRate },
                { key: 7, parameter: "Spot UV Rate", value: spotUvRate, total: topRate + restRate + printRate + fixedCost + laminationRate + leafingRate + spotUvRate },
                { key: 8, parameter: "Net Rate", value: finalRate, total: finalRate },
              ];

              const columns = [
                { title: "Rate Components", dataIndex: "parameter", key: "parameter", width: "40%",
                  render: (text, record) => record.key === 8 ? <Text strong>{text}</Text> : text },
                { title: "Piecewise Rate", dataIndex: "value", key: "value", width: "30%",
                  render: (value, record) => <Text strong={record.key === 8}>₹{value.toFixed(2)}</Text> },
                { title: "Total Rate", dataIndex: "total", key: "total", width: "30%", align: "right",
                  render: (total, record) => <Text strong={record.key === 8} style={record.key === 8 ? { color: "#52c41a", fontSize: 14 } : {}}>₹{total.toFixed(2)}</Text> },
              ];

              return (
                <Table
                  dataSource={rateData}
                  columns={columns}
                  pagination={false}
                  size="small"
                  bordered
                />
              );
            })()}

            {/* Margin indicator */}
            {watchedRate > 0 && (
              <div style={{ marginTop: 8, padding: "6px 12px", borderRadius: 4, background: watchedRate >= calculationResult.finalRate ? "#f6ffed" : "#fff2f0" }}>
                <Text style={{ fontSize: 13, color: watchedRate >= calculationResult.finalRate ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
                  {watchedRate >= calculationResult.finalRate
                    ? `✓ Margin: ₹${(watchedRate - calculationResult.finalRate).toFixed(2)} per unit (+${(((watchedRate - calculationResult.finalRate) / calculationResult.finalRate) * 100).toFixed(1)}%)`
                    : `⚠ Loss: ₹${(calculationResult.finalRate - watchedRate).toFixed(2)} per unit (-${(((calculationResult.finalRate - watchedRate) / calculationResult.finalRate) * 100).toFixed(1)}%)`}
                </Text>
              </div>
            )}
          </Card>
        )}

        {/* ====== Actions ====== */}
        <Divider />
        <Row justify="end">
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSave} shape="round">
              {initialData
                ? "Update Item"
                : addAsGroup && groupInfo
                ? `Add Group (${groupInfo.members?.length} items)`
                : "Add Item"}
            </Button>
          </Space>
        </Row>
      </Form>
    </Drawer>
  );
};

export default OrderItemModal;
